import { t } from "i18next";

import type { ColumnsClarFactory } from "@/core/components/shared/data-table";
import { Badge } from "@/core/components/ui/badge";
import { NoTranslate } from "@/core/components/ui/no-translate";
import type { MerchantSalesAgentDto } from "@/features/merchant/api/dto";
import {
  SALES_AGENT_STATUSES,
  type SalesAgentStatus,
} from "@/features/shared/api/enums";

export type SalesAgentRow = MerchantSalesAgentDto & { id: string };

const selectOptions = (values: readonly string[]) =>
  values.map((value) => ({ value, label: value }));

const STATUS_VARIANTS: Record<
  SalesAgentStatus,
  "success" | "secondary" | "destructive"
> = {
  ACTIVE: "success",
  INACTIVE: "secondary",
  SUSPENDED: "destructive",
};

/**
 * Colonnes des agents de vente d'un marchand
 * (`GET /MerchantSalesAgents/merchant/{merchantId}`).
 */
export const salesAgentsColumnsFactory = (
  renderActions: (agent: SalesAgentRow) => React.ReactNode
): ColumnsClarFactory<SalesAgentRow> =>
  () => [
    {
      id: "fullName",
      accessorKey: "fullName",
      header: t("merchant.salesAgents.columns.fullName"),
      cell: ({ getValue }) => (
        <NoTranslate as="span" className="font-medium">
          {(getValue() as string) || "—"}
        </NoTranslate>
      ),
      meta: { type: "text" },
    },
    {
      id: "partyId",
      accessorKey: "partyId",
      header: t("merchant.salesAgents.columns.partyId"),
      cell: ({ getValue }) => (
        <NoTranslate as="span" className="font-mono text-xs">
          {(getValue() as string) || "—"}
        </NoTranslate>
      ),
      meta: { type: "text" },
    },
    {
      id: "keycloakUserId",
      accessorKey: "keycloakUserId",
      header: t("merchant.salesAgents.columns.keycloakUserId"),
      cell: ({ getValue }) => (
        <NoTranslate as="span" className="font-mono text-xs">
          {(getValue() as string) || "—"}
        </NoTranslate>
      ),
      meta: { type: "text" },
    },
    {
      id: "storeId",
      accessorKey: "storeId",
      header: t("merchant.salesAgents.columns.storeId"),
      cell: ({ getValue }) => (
        <NoTranslate as="span" className="font-mono text-xs">
          {(getValue() as string) || "—"}
        </NoTranslate>
      ),
      meta: { type: "text" },
    },
    {
      id: "status",
      accessorKey: "status",
      header: t("merchant.salesAgents.columns.status"),
      cell: ({ getValue }) => {
        const status = getValue() as SalesAgentStatus | undefined;
        return status ? (
          <NoTranslate as="span">
            <Badge variant={STATUS_VARIANTS[status] ?? "outline"}>
              {status}
            </Badge>
          </NoTranslate>
        ) : (
          "—"
        );
      },
      meta: { type: "select", options: selectOptions(SALES_AGENT_STATUSES) },
    },
    {
      id: "actions",
      header: t("common.actions"),
      enableHiding: false,
      cell: ({ row }) => renderActions(row.original),
      meta: { type: "denied" },
    },
  ];
